/**
 * SourceImpactPanel — "this source document was updated: which sections read
 * it, and redraft them".
 *
 * Lists the run's sections whose current draft read ``documentName``
 * (``GET /runs/{id}/source-impact``), grouped High / Medium / Low, each row
 * opening to the pages and excerpts it read. High rows start ticked. The
 * redraft goes through the caller's ``onRedraft`` — the shell's existing
 * continue-sections path, so sections stream into Word exactly like "Draft
 * more". Rows then follow the spine: "redrafting" until the module's existing
 * section-final/draft-ready signal lands, then "redrafted", and the impact
 * list is fetched again.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchSourceImpact, type SourceImpactResponse, type SourceImpactSection } from "../../api/client";
import type { SpineSection } from "../hooks/useVariantCRun";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";
import {
  advanceRedraftStates,
  buildRedraftRequest,
  defaultInstruction,
  defaultTicked,
  describeRedraftError,
  groupByStrength,
  hitPages,
  newlyRedrafted,
  type RedraftRequest,
  type RedraftStates,
  STRENGTH_LABEL,
  sectionLabel,
  sectionPages,
  startRedraft,
} from "./sourceImpactModel";

/** What the shell's run-start primitive did with a redraft request. */
export type RedraftOutcome = { status: "started" } | { status: "failed"; error: unknown } | { status: "not_started" };

export type RedraftHandler = (req: RedraftRequest) => Promise<RedraftOutcome>;

export interface SourceImpactPanelProps {
  runId: string;
  documentName: string;
  /** Redraft the ticked modules with the instruction (continue-sections). */
  onRedraft: RedraftHandler;
  /** The live spine — drives redrafting → redrafted. */
  spine?: readonly SpineSection[];
  onClose?: () => void;
  /** Injectable for tests. */
  fetchImpact?: typeof fetchSourceImpact;
}

const STRENGTH_COLOR: Record<string, string> = {
  high: statusColors.danger,
  medium: statusColors.warn,
  low: inkColors.ink4,
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    fontFamily: fonts.body,
    color: inkColors.ink2,
    fontSize: "12px",
    minHeight: 0,
  },
  header: { display: "flex", alignItems: "flex-start", columnGap: "8px" },
  title: {
    flex: 1,
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    lineHeight: "1.35",
    wordBreak: "break-word",
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: inkColors.ink3,
    fontSize: "16px",
    lineHeight: "1",
    padding: "2px 4px",
  },
  note: {
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    padding: "8px 10px",
    color: inkColors.ink3,
    lineHeight: "1.4",
  },
  error: {
    border: `1px solid ${statusColors.danger}`,
    borderRadius: "6px",
    padding: "8px 10px",
    color: statusColors.danger,
    lineHeight: "1.4",
  },
  groupHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: inkColors.ink3,
    marginTop: "4px",
  },
  dot: { width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0 },
  list: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", rowGap: "4px" },
  row: { border: `1px solid ${surfaceColors.line}`, borderRadius: "6px", backgroundColor: surfaceColors.paper },
  rowMain: { display: "flex", alignItems: "center", columnGap: "8px", padding: "6px 8px" },
  rowLabel: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", rowGap: "2px" },
  rowTitle: { color: inkColors.ink, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" },
  rowMeta: { color: inkColors.ink4, fontSize: "11px" },
  expandBtn: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: brandColors.primary,
    fontSize: "11px",
    padding: "2px 4px",
    flexShrink: 0,
  },
  badge: { fontSize: "10px", fontWeight: 700, borderRadius: "10px", padding: "1px 7px", flexShrink: 0 },
  badgeRedrafting: { backgroundColor: brandColors.light, color: brandColors.primary },
  badgeRedrafted: { backgroundColor: "var(--success-soft)", color: "var(--success)" },
  hits: {
    borderTop: `1px solid ${surfaceColors.line}`,
    padding: "6px 8px 8px 30px",
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  hitMeta: { color: inkColors.ink3, fontSize: "11px" },
  excerpt: {
    margin: "2px 0 0",
    padding: "4px 6px",
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink2,
    fontSize: "11px",
    lineHeight: "1.4",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  label: { fontWeight: 600, color: inkColors.ink2 },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "64px",
    resize: "vertical",
    fontFamily: fonts.body,
    fontSize: "12px",
    padding: "6px 8px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink,
  },
  actions: { display: "flex", columnGap: "8px", justifyContent: "flex-end", flexWrap: "wrap", rowGap: "6px" },
  primaryBtn: {
    border: "none",
    borderRadius: "6px",
    padding: "7px 12px",
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    fontWeight: 600,
    cursor: "pointer",
    ":disabled": { opacity: 0.5, cursor: "default" },
  },
  dangerBtn: { backgroundColor: statusColors.danger },
  secondaryBtn: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "6px",
    padding: "7px 12px",
    backgroundColor: surfaceColors.paper,
    color: inkColors.ink2,
    cursor: "pointer",
  },
  confirm: {
    border: `1px solid ${statusColors.warn}`,
    borderRadius: "6px",
    padding: "8px 10px",
    backgroundColor: "var(--warning-soft)",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    lineHeight: "1.4",
  },
});

export const SourceImpactPanel: React.FC<SourceImpactPanelProps> = ({
  runId,
  documentName,
  onRedraft,
  spine,
  onClose,
  fetchImpact = fetchSourceImpact,
}) => {
  const styles = useStyles();
  const [data, setData] = useState<SourceImpactResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [instruction, setInstruction] = useState(() => defaultInstruction(documentName));
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redraftError, setRedraftError] = useState<string | null>(null);
  const [redraft, setRedraftState] = useState<RedraftStates>({});
  const redraftRef = useRef<RedraftStates>({});
  const setRedraft = useCallback((next: RedraftStates) => {
    redraftRef.current = next;
    setRedraftState(next);
  }, []);
  const [reloadNonce, setReloadNonce] = useState(0);
  // Ticks are seeded from the first load per document only; a re-fetch after
  // a redraft keeps the writer's choices.
  const seededRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadNonce re-runs the fetch on purpose
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setLoadError(null);
    fetchImpact(runId, documentName, ctrl.signal)
      .then((resp) => {
        if (ctrl.signal.aborted) return;
        setData(resp);
        if (!seededRef.current) {
          seededRef.current = true;
          setTicked(defaultTicked(resp.sections));
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [runId, documentName, fetchImpact, reloadNonce]);

  // New document → fresh instruction, ticks and lifecycle.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runId/documentName are the reset triggers
  useEffect(() => {
    seededRef.current = false;
    setData(null);
    setExpanded(new Set());
    setInstruction(defaultInstruction(documentName));
    setRedraft({});
    setConfirming(false);
    setRedraftError(null);
  }, [documentName, runId, setRedraft]);

  // Follow the spine: redrafting → redrafted, then fetch the list again.
  useEffect(() => {
    if (!spine) return;
    const prev = redraftRef.current;
    const next = advanceRedraftStates(prev, spine);
    if (next === prev) return;
    setRedraft(next);
    if (newlyRedrafted(prev, next).length > 0) setReloadNonce((n) => n + 1);
  }, [spine, setRedraft]);

  const sections: SourceImpactSection[] = data?.sections ?? [];
  const groups = useMemo(() => groupByStrength(sections), [sections]);
  const request = useMemo(() => buildRedraftRequest(sections, ticked, instruction), [sections, ticked, instruction]);

  const toggle = useCallback((id: string) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setConfirming(false);
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const runRedraft = useCallback(async () => {
    if (!request) return;
    setSubmitting(true);
    setRedraftError(null);
    try {
      const outcome = await onRedraft(request);
      if (outcome.status === "started") {
        setRedraft(startRedraft(redraftRef.current, request.module_ids));
        setConfirming(false);
      } else if (outcome.status === "failed") {
        setRedraftError(describeRedraftError(outcome.error));
      } else {
        setRedraftError("Redraft did not start — see the message in the main view.");
      }
    } catch (err) {
      setRedraftError(describeRedraftError(err));
    } finally {
      setSubmitting(false);
    }
  }, [onRedraft, request, setRedraft]);

  const tickedCount = request?.module_ids.length ?? 0;

  return (
    <section
      className={styles.root}
      aria-label={`Sections that read ${documentName}`}
      data-testid="source-impact-panel"
    >
      <div className={styles.header}>
        <div className={styles.title}>Sections that read “{documentName}”</div>
        {onClose && (
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
      </div>

      {data?.names_may_be_partial && (
        <div className={styles.note} data-testid="source-impact-partial">
          This run predates full document-name capture: a section whose library search used this document as a secondary
          hit may be missing from the list.
        </div>
      )}

      {loading && !data && <div className={styles.note}>Finding sections that read this document…</div>}
      {loadError && <div className={styles.error}>Couldn't load impacted sections: {loadError}</div>}
      {data && !loading && sections.length === 0 && (
        <div className={styles.note}>No drafted section of this run read this document.</div>
      )}

      {groups.map((g) => (
        <div key={g.strength}>
          <div className={styles.groupHead}>
            <span className={styles.dot} style={{ backgroundColor: STRENGTH_COLOR[g.strength] }} aria-hidden="true" />
            {STRENGTH_LABEL[g.strength]} · {g.sections.length}
          </div>
          <ul className={styles.list}>
            {g.sections.map((s) => {
              const phase = redraft[s.module_id]?.phase;
              const isOpen = expanded.has(s.module_id);
              const pages = sectionPages(s);
              return (
                <li key={s.module_id} className={styles.row} data-testid={`impact-row-${s.module_id}`}>
                  <div className={styles.rowMain}>
                    <input
                      type="checkbox"
                      checked={ticked.has(s.module_id)}
                      onChange={() => toggle(s.module_id)}
                      aria-label={`Redraft ${sectionLabel(s)}`}
                      disabled={submitting}
                    />
                    <div className={styles.rowLabel}>
                      <span className={styles.rowTitle} title={sectionLabel(s)}>
                        {sectionLabel(s)}
                      </span>
                      <span className={styles.rowMeta}>
                        {s.hit_count} {s.hit_count === 1 ? "read" : "reads"}
                        {s.tools.length > 0 ? ` · ${s.tools.join(", ")}` : ""}
                      </span>
                    </div>
                    {phase === "redrafting" && (
                      <span className={mergeClasses(styles.badge, styles.badgeRedrafting)}>Redrafting…</span>
                    )}
                    {phase === "redrafted" && (
                      <span className={mergeClasses(styles.badge, styles.badgeRedrafted)}>Redrafted</span>
                    )}
                    <button
                      type="button"
                      className={styles.expandBtn}
                      onClick={() => toggleExpanded(s.module_id)}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? "Hide" : "Details"}
                    </button>
                  </div>
                  {isOpen && (
                    <div className={styles.hits}>
                      {pages.length > 0 && <div className={styles.hitMeta}>Pages read: {pages.join(", ")}</div>}
                      {s.hits.map((h) => {
                        const where = hitPages(h);
                        return (
                          <div key={h.transaction_id}>
                            <div className={styles.hitMeta}>
                              {h.tool}
                              {h.via ? ` via ${h.via}` : ""}
                              {where ? ` · ${where}` : ""}
                              {h.query ? ` · “${h.query}”` : ""}
                            </div>
                            {h.excerpt && <blockquote className={styles.excerpt}>{h.excerpt}</blockquote>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {sections.length > 0 && (
        <>
          <label className={styles.label} htmlFor="source-impact-instruction">
            Instruction for the redraft
          </label>
          <textarea
            id="source-impact-instruction"
            className={styles.textarea}
            value={instruction}
            onChange={(e) => {
              setInstruction(e.target.value);
              setConfirming(false);
            }}
            disabled={submitting}
          />

          {redraftError && (
            <div className={styles.error} role="alert">
              {redraftError}
            </div>
          )}

          {confirming ? (
            <div className={styles.confirm} role="alertdialog" aria-label="Confirm redraft">
              <div>
                Redraft {tickedCount} section{tickedCount === 1 ? "" : "s"}? Redrafting <strong>replaces</strong> each
                section's text in the document, <strong>including any manual edits</strong> made since it was drafted.
              </div>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={mergeClasses(styles.primaryBtn, styles.dangerBtn)}
                  onClick={() => void runRedraft()}
                  disabled={submitting || !request}
                >
                  {submitting ? "Starting…" : "Replace and redraft"}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => setConfirming(true)}
                disabled={!request || submitting}
              >
                Redraft selected{tickedCount > 0 ? ` (${tickedCount})` : ""}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default SourceImpactPanel;
